import config from "config";
import ko from "knockout";
import Promise from "bluebird";
import _ from "lodash";
import moment from "moment-timezone";
import ExponentialIntervalGenerator from "util/ExponentialIntervalGenerator";
import apiCore from "services/apiCore";

const LOG_LEVELS = ["trace", "debug", "info", "warn", "error"];
const TRUNCATED_TRACE_MESSAGE = "TRUNCATED LOG: ";
const logBuffer = ko.observableArray([]);
const inLoggingTimeout = ko.observable(false);
const console = window.console;
const exponentialBackoff = new ExponentialIntervalGenerator(
    config.maxServerLogInterval,
    config.serverLogInterval
);
let maxTracesToLog;

function isError(object) {
    return object instanceof Error;
}

function typeConverter(key, item) {
    if (item === undefined || item === null) {
        return item;
    } else if (isError(item)) {
        return {
            name: item.name,
            message: item.message,
            stack: item.stack,
        };
    } else {
        return item;
    }
}

function createTrace(level, logItem) {
    let message;
    if (_.isArray(logItem) || _.isObject(logItem)) {
        try {
            message = JSON.stringify(logItem, typeConverter);
        } catch (err) {
            message = JSON.stringify(
                ["Error serializing log data", err],
                typeConverter
            );
        }
    } else {
        message = logItem.toString();
    }

    return {
        topic: config.appName + "-log",
        level: level.toUpperCase(),
        time: moment().toISOString(),
        message: message,
    };
}

function addStackIfNotPresent(itemsToLog) {
    if (!_.some(itemsToLog, isError)) {
        //Full try-catch seems unnecessary here, but phantom requires it,
        //so other browsers might also.
        try {
            throw new Error("Logger-added stack");
        } catch (err) {
            itemsToLog.push(err);
        }
    }
}

function bufferLog(/* level, itemsToLog... */) {
    const level = arguments[0];
    const itemsToLog = Array.prototype.slice.call(arguments, 1);

    if (level == "error") {
        addStackIfNotPresent(itemsToLog);
    }

    const trace = createTrace(level, itemsToLog);
    logBuffer.push(trace);
}

function decreaseLogLimit(currentLimit) {
    maxTracesToLog = Math.floor(currentLimit / 2);
}

function resetLogLimit() {
    maxTracesToLog = undefined;
}

function getTracesTolog() {
    if (maxTracesToLog === 0) {
        //If a single trace was too big to log.
        const traces = logBuffer.splice(0, 1);
        const trace = traces[0];
        trace.message = (TRUNCATED_TRACE_MESSAGE + trace.message).slice(
            0,
            config.maxServerLogSize
        );
        logBuffer.unshift(
            createTrace("error", "Log message truncated due to size limit.")
        );
        return traces;
    } else {
        const toLogCount = maxTracesToLog || logBuffer().length;
        return logBuffer().splice(0, toLogCount);
    }
}

function sendLogs() {
    return new Promise(function (resolve) {
        const toSend = getTracesTolog();

        const payload = {
            app: {
                appId: config.appName,
                appVersion: config.appVersion,
            },
            traces: toSend,
        };

        return apiCore.v2
            .post("/diagnostics/trace", payload)
            .then(function () {
                resetLogLimit();
                exponentialBackoff.reset();
            })
            .catch(function (error) {
                if (error.type == "http" && error.details.status == 413) {
                    decreaseLogLimit(toSend.length);
                    console.error(
                        "# of logs to send limited due to server size limit"
                    );
                } else {
                    console.error("Failed to log traces", error);
                    exponentialBackoff.fail();
                }
                logBuffer(toSend.concat(logBuffer())); //re-append entries for future logging.
            })
            .finally(function () {
                resolve();
            });
    });
}

const serverLogger = {
    isLoggedIn: ko.observable(false), //Allows authService to signal when logging can begin
};
LOG_LEVELS.forEach(function (level) {
    serverLogger[level] = _.bind(bufferLog, null, level);
});

ko.computed(function () {
    if (
        serverLogger.isLoggedIn() &&
        logBuffer().length > 0 &&
        !inLoggingTimeout()
    ) {
        inLoggingTimeout(true);
        sendLogs().then(function () {
            setTimeout(function () {
                inLoggingTimeout(false);
            }, exponentialBackoff.getInterval());
        });
    }
});

export default serverLogger;
